/**
 * tiendaAsistentePresets — Contenido de ejemplo predefinido del asistente.
 *
 * QUÉ HACE: centraliza los textos genéricos con los que nace el asistente
 *   (saludo, fallback, no encontrado), las reglas/FAQ de ejemplo y el arranque
 *   de «Conócenos». Sirve para que la configuración nunca se vea vacía y el
 *   dueño solo edite lo suyo. Todo es genérico y sirve para cualquier nicho.
 * QUÉ INVOCA: nada (solo constantes tipadas).
 * QUÉ EXPORTA: getAsistenteDefaults, getSobreNosotrosDefaults,
 *   migrarAsistenteConfig, migrarSobreNosotros y TIENDA_PRESETS_VERSION.
 *   Los presets individuales son constantes internas del archivo.
 * QUÉ NO DEBE HACER: no accede a storage ni renderiza.
 */
import type {
  TiendaAsistenteConfig,
  TiendaAsistenteFaq,
  TiendaRespuestaPersonalizada,
  TiendaSobreNosotros,
} from "../types/tienda.types";

const ASISTENTE_SALUDO_PRESET =
  "Pregúntame por productos, precios, características, disponibilidad, envíos, pagos u horario.";

const ASISTENTE_FALLBACK_PRESET =
  "Pregúntame por un producto, su precio o características, la disponibilidad, el envío, los métodos de pago, el horario o las promociones.";

const ASISTENTE_NO_ENCONTRADO_PRESET =
  'No encontré "{busqueda}" en el catálogo. Prueba con otro nombre o mira las categorías disponibles; también puedes escribirnos por WhatsApp.';

const ASISTENTE_REGLAS_PRESET: TiendaRespuestaPersonalizada[] = [
  {
    id: "preset_regla_gracias",
    activadores: "gracias, agradecido, muy amable",
    respuesta: "¡Con gusto! Gracias a ti por preferirnos.",
    activo: true,
  },
  {
    id: "preset_regla_persona",
    activadores: "hablar con alguien, asesor, una persona, humano",
    respuesta:
      "Claro, toca el botón de WhatsApp y te atiende una persona del equipo.",
    activo: true,
  },
];

const ASISTENTE_FAQS_PRESET: TiendaAsistenteFaq[] = [
  {
    id: "preset_faq_comprobante",
    pregunta: "¿Entregan comprobante del pedido?",
    respuesta:
      "Sí. Al confirmar tu pedido generamos el comprobante con el detalle y el total.",
  },
  {
    id: "preset_faq_devoluciones",
    pregunta: "¿Aceptan devoluciones o cambios?",
    respuesta:
      "Sí. Escríbenos por WhatsApp con el número de tu pedido y te ayudamos con el cambio o la devolución.",
  },
  {
    id: "preset_faq_reservar",
    pregunta: "¿Puedo reservar o apartar un pedido?",
    respuesta:
      "Sí. Escríbenos por WhatsApp indicando los productos y la hora en que los recogerás.",
  },
  {
    id: "preset_faq_parqueadero",
    pregunta: "¿Tienen parqueadero?",
    respuesta:
      "Sí, contamos con parqueadero. Si necesitas indicaciones, escríbenos por WhatsApp.",
  },
];

/**
 * FAQ de pedido de presets viejos. El motor responde «¿Cómo hago un pedido?»
 * con instrucciones + recomendados, así que la migración la retira solo si el
 * dueño no la editó (mismo id, pregunta y respuesta).
 */
const ASISTENTE_FAQ_PEDIDO_LEGACY = {
  id: "preset_faq_pedido",
  pregunta: "¿Cómo hago un pedido?",
  respuesta:
    "Agrega tus productos al carrito y toca «Completar pedido». Si prefieres, escríbenos por WhatsApp.",
};

/**
 * FAQ de efectivo de presets viejos. El motor ya conoce los métodos de pago
 * reales (`answerPagos`), así que la migración la retira solo si el dueño no la
 * editó; si la editó, se respeta.
 */
const ASISTENTE_FAQ_EFECTIVO_LEGACY = {
  id: "preset_faq_efectivo",
  pregunta: "¿Puedo pagar en efectivo?",
  respuesta:
    "Sí. Al confirmar tu pedido verás los métodos de pago disponibles.",
};

const SOBRE_NOSOTROS_HISTORIA_PRESET =
  "Somos un negocio local comprometido con la calidad y el buen servicio. Cada compra nos ayuda a seguir creciendo. ¡Gracias por preferirnos!";

const SOBRE_NOSOTROS_SELLOS_PRESET = [
  "Atención cercana",
  "Productos de calidad",
];

/** Fotos de ejemplo de la galería (URLs estables, mismo patrón que productos). */
const SOBRE_NOSOTROS_GALERIA_PRESET = [
  "https://picsum.photos/seed/altenot-local-1/800/600",
  "https://picsum.photos/seed/altenot-local-2/800/600",
  "https://picsum.photos/seed/altenot-local-3/800/600",
  "https://picsum.photos/seed/altenot-local-4/800/600",
];

/** Equipo de ejemplo (editable por el dueño). */
const SOBRE_NOSOTROS_EQUIPO_PRESET = [
  { nombre: "María", rol: "Atención al cliente" },
  { nombre: "Carlos", rol: "Administración" },
];

/**
 * Recomendados de arranque: IDs del catálogo demo (Almuerzo, Desayuno
 * Completo, Coca-Cola, Agua, Caldo de Costilla, Moñona y Huevos al Gusto).
 * En catálogos reales los IDs que no existan se ignoran y el dueño reselecciona
 * desde el buscador del asistente.
 */
const RECOMENDADOS_IDS_PRESET = [
  "cat_rest_10",
  "cat_desay_4",
  "cat_extra_11",
  "cat_extra_17",
  "cat_desay_0",
  "cat_desay_6",
  "cat_desay_5",
];

/** Extras/complementos de arranque (fruta y postre del catálogo demo). */
const EXTRAS_IDS_PRESET = ["cat_extra_5", "cat_hela_8"];

/**
 * Versión del contenido de ejemplo. Súbela cuando cambien los presets para que
 * las configs ya guardadas vuelvan a rellenar solo sus huecos vacíos.
 */
export const TIENDA_PRESETS_VERSION = 6;

/** Configuración completa del asistente con todo el contenido de ejemplo. */
export const getAsistenteDefaults = (): TiendaAsistenteConfig => ({
  activo: true,
  nombre: "Asistente de la tienda",
  saludo: ASISTENTE_SALUDO_PRESET,
  mensajeFallback: ASISTENTE_FALLBACK_PRESET,
  mensajeNoEncontrado: ASISTENTE_NO_ENCONTRADO_PRESET,
  whatsappHandoff: true,
  recomendadosActivo: true,
  recomendadosEtiqueta: "Recomendados",
  mensajeRecomendados: "",
  recomendadosIds: [...RECOMENDADOS_IDS_PRESET],
  recomendadosPorHorario: true,
  extrasIds: [...EXTRAS_IDS_PRESET],
  reglas: ASISTENTE_REGLAS_PRESET.map((regla) => ({ ...regla })),
  faqs: ASISTENTE_FAQS_PRESET.map((faq) => ({ ...faq })),
  presetsCargados: true,
  presetsVersion: TIENDA_PRESETS_VERSION,
});

/** Contenido «Conócenos» de arranque, visible desde el primer momento. */
export const getSobreNosotrosDefaults = (): TiendaSobreNosotros => ({
  activo: true,
  titulo: "Conócenos",
  historia: SOBRE_NOSOTROS_HISTORIA_PRESET,
  sellos: [...SOBRE_NOSOTROS_SELLOS_PRESET],
  galeria: [...SOBRE_NOSOTROS_GALERIA_PRESET],
  equipo: SOBRE_NOSOTROS_EQUIPO_PRESET.map((miembro) => ({ ...miembro })),
});

/** true si la FAQ coincide exactamente con un preset sin editar. */
const esFaqLegacy = (
  faq: TiendaAsistenteFaq,
  legacy: { id: string; pregunta: string; respuesta: string },
): boolean =>
  faq.id === legacy.id &&
  faq.pregunta === legacy.pregunta &&
  faq.respuesta === legacy.respuesta;

/** Presets de FAQ conocidos (actuales y legados), para detectar sin editar. */
const FAQ_PRESETS_CONOCIDOS = [
  ...ASISTENTE_FAQS_PRESET,
  ASISTENTE_FAQ_PEDIDO_LEGACY,
  ASISTENTE_FAQ_EFECTIVO_LEGACY,
];

/** true si la FAQ es un preset intacto (mismo id y mismo texto). */
const esFaqPresetSinEditar = (faq: TiendaAsistenteFaq): boolean =>
  FAQ_PRESETS_CONOCIDOS.some((preset) => esFaqLegacy(faq, preset));

/**
 * Migra la lista de FAQ: quita los presets viejos que el motor ya responde
 * mejor (pedido y efectivo) solo si quedaron sin editar. Si todo lo guardado
 * son presets intactos, adopta el set actual completo (así llegan las preguntas
 * de ejemplo recién agregadas); si el dueño tiene preguntas propias o editadas,
 * se respetan tal cual para no resucitar ejemplos que haya borrado.
 */
const migrarFaqs = (
  faqs: TiendaAsistenteFaq[] | undefined,
  base: TiendaAsistenteFaq[],
): TiendaAsistenteFaq[] => {
  const limpias = (faqs ?? []).filter(
    (faq) =>
      !esFaqLegacy(faq, ASISTENTE_FAQ_PEDIDO_LEGACY) &&
      !esFaqLegacy(faq, ASISTENTE_FAQ_EFECTIVO_LEGACY),
  );
  if (limpias.length === 0) return base;
  return limpias.every(esFaqPresetSinEditar) ? base : limpias;
};

/**
 * Migra el asistente a la versión actual de presets: rellena solo los huecos
 * vacíos (nunca pisa lo editado) y marca la versión. Idempotente.
 */
export const migrarAsistenteConfig = (
  stored: TiendaAsistenteConfig | undefined,
): TiendaAsistenteConfig => {
  const base = getAsistenteDefaults();
  if (!stored) return base;
  const merged: TiendaAsistenteConfig = { ...base, ...stored };
  if (stored.presetsVersion === TIENDA_PRESETS_VERSION) return merged;
  return {
    ...merged,
    saludo: merged.saludo?.trim() ? merged.saludo : base.saludo,
    mensajeFallback: merged.mensajeFallback?.trim()
      ? merged.mensajeFallback
      : base.mensajeFallback,
    mensajeNoEncontrado: merged.mensajeNoEncontrado?.trim()
      ? merged.mensajeNoEncontrado
      : base.mensajeNoEncontrado,
    reglas:
      merged.reglas && merged.reglas.length > 0 ? merged.reglas : base.reglas,
    faqs: migrarFaqs(merged.faqs, base.faqs ?? []),
    // Rellena las selecciones vacías con los ejemplos del catálogo demo.
    recomendadosIds:
      merged.recomendadosIds && merged.recomendadosIds.length > 0
        ? merged.recomendadosIds
        : base.recomendadosIds,
    extrasIds:
      merged.extrasIds && merged.extrasIds.length > 0
        ? merged.extrasIds
        : base.extrasIds,
    presetsCargados: true,
    presetsVersion: TIENDA_PRESETS_VERSION,
  };
};

/**
 * Migra «Conócenos» con el mismo criterio. La versión se lee del asistente
 * (comparten ciclo); al migrar se activa la sección y se rellenan galería,
 * equipo, historia y sellos solo si están vacíos.
 */
export const migrarSobreNosotros = (
  stored: TiendaSobreNosotros | undefined,
  asistenteVersion: number | undefined,
): TiendaSobreNosotros => {
  const base = getSobreNosotrosDefaults();
  if (!stored) return base;
  const merged: TiendaSobreNosotros = { ...base, ...stored };
  if (asistenteVersion === TIENDA_PRESETS_VERSION) return merged;
  return {
    ...merged,
    activo: true,
    titulo: merged.titulo?.trim() ? merged.titulo : base.titulo,
    historia: merged.historia?.trim() ? merged.historia : base.historia,
    sellos:
      merged.sellos && merged.sellos.length > 0 ? merged.sellos : base.sellos,
    galeria:
      merged.galeria && merged.galeria.length > 0
        ? merged.galeria
        : base.galeria,
    equipo:
      merged.equipo && merged.equipo.length > 0 ? merged.equipo : base.equipo,
  };
};
